import React from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  styled,
  Alert,
} from '@mui/material';
import { Row } from 'further-ui/components/Layout';
import Avatar from 'further-ui/components/Avatar';
import { Controller } from 'react-hook-form';
import { AppNumberInput } from 'components/FormElements';
import CheckboxCell from 'components/EqualisationElements/CheckboxCell';
import useAddCommitmentsToEqualisations from '../../../hooks/useAddCommitmentsToEqualisations';
import { round, sumBy } from 'lodash';
import { numberToCurrencyString } from 'further-ui/utils';
import { useLayoutContext } from 'contexts/LayoutContext';
import PageSection from 'components/PageSection';
import { CapitalContributionRule } from 'further-types/lp/vehicle';
import PreviouslyClosedCommitmentsTable from './PreviouslyClosedCommitmentsTable';
import api from 'lib/trpcClient';

const StyledTable = styled(Table)(() => ({
  width: '100%',
}));

const StyledTableBody = styled(TableBody)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightLight,
  border: '1px solid #ebebec',
  borderRadius: '8px',
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.md),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledTableHeaderCell = styled(StyledTableCell)(() => ({
  paddingBottom: 0,
  paddingTop: 0,
}));

const CommitmentsTable: React.FC = () => {
  const { vehicle } = useLayoutContext();
  const [equalisationsData] = api.equalisations.list.useSuspenseQuery({
    vehicleId: vehicle?._id ?? '',
  });

  const {
    control,
    formCommitments,
    previouslyClosedCommitments,
    pendingCloseCommitments,
    baseCurrency,
    handleSelectAll,
    handleOnChangeIsSelected,
    isAllSelected,
    isIndeterminate,
    isCapitalAndLoan,
    capitalCommitmentIsEditable,
    capitalCommitmentOnChange,
    loanCommitmentIsEditable,
    loanCommitmentOnChange,
    totalCommitmentIsEditable,
    totalCommitmentOnChange,
    capitalContributionRuleOverrideOnChange,
    showOverrideColumn,
    showOverrideCheckbox,
  } = useAddCommitmentsToEqualisations(vehicle);

  const sumOfSelectedCommitmentsCapitalCommitmentAmount = round(
    sumBy(
      formCommitments?.filter((c) => c.isSelected),
      'capitalCommitmentLcy',
    ) ?? 0,
    2,
  );

  const sumOfTotalCommitmentLcyForSelectedCommitments = round(
    sumBy(
      formCommitments?.filter(
        (c) => c.isSelected && !c.isCarryPartnerCommitment,
      ),
      'totalCommitmentLcy',
    ) ?? 0,
    2,
  );

  const onlyCarryPartnerIsSelected = formCommitments
    ?.filter((c) => c.isSelected)
    .every((c) => c.isCarryPartnerCommitment);

  const shouldShowFixedCapitalAmountWillIncreaseOnSubmitMessage =
    !!vehicle?.fixedCapitalAmount &&
    sumOfSelectedCommitmentsCapitalCommitmentAmount >
      vehicle?.fixedCapitalAmount;

  const shouldShowFixedCapitalAmountWillDecreaseOnSubmitMessage =
    !!vehicle?.fixedCapitalAmount &&
    sumOfTotalCommitmentLcyForSelectedCommitments <
      vehicle?.fixedCapitalAmount &&
    !onlyCarryPartnerIsSelected;

  const fixedCapitalAmountWillChangeOnSubmitMessage = `The capital contribution allocated above is higher than the fixed capital input on the Fund Setup page. If you submit this page, the vehicle's fixed capital will increase from ${numberToCurrencyString(
    vehicle?.fixedCapitalAmount,
    {
      currency: baseCurrency,
    },
  )} to ${numberToCurrencyString(
    sumOfSelectedCommitmentsCapitalCommitmentAmount,
    {
      currency: baseCurrency,
    },
  )}.`;

  const fixedCapitalAmountWillDecreaseOnSubmitMessage = `The total commitment amount being closed is lower than the fixed capital input on the Fund Setup page. If you submit this page, the vehicle's fixed capital will decrease from ${numberToCurrencyString(
    vehicle?.fixedCapitalAmount,
    {
      currency: baseCurrency,
    },
  )} to ${numberToCurrencyString(
    sumOfSelectedCommitmentsCapitalCommitmentAmount,
    {
      currency: baseCurrency,
    },
  )}.`;

  const shouldShowPreviouslyClosedCommitmentsTable =
    isCapitalAndLoan &&
    vehicle?.capitalContributionRule !==
      CapitalContributionRule.FixedCommitmentPercentage &&
    !!equalisationsData?.results?.length;

  return (
    <>
      {shouldShowPreviouslyClosedCommitmentsTable && (
        <PreviouslyClosedCommitmentsTable
          previouslyClosedCommitments={previouslyClosedCommitments}
          baseCurrency={baseCurrency}
          capitalCommitmentIsEditable={capitalCommitmentIsEditable}
          capitalCommitmentOnChange={capitalCommitmentOnChange}
          loanCommitmentIsEditable={loanCommitmentIsEditable}
          loanCommitmentOnChange={loanCommitmentOnChange}
          capitalContributionRuleOverrideOnChange={
            capitalContributionRuleOverrideOnChange
          }
          showOverrideColumn={showOverrideColumn}
          showOverrideCheckbox={showOverrideCheckbox}
        />
      )}
      <PageSection title="Select commitments" withSpacing>
        <TableContainer>
          <StyledTable>
            <TableHead>
              <TableRow>
                <StyledTableHeaderCell padding="checkbox">
                  <Checkbox
                    indeterminate={isIndeterminate}
                    checked={isAllSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    color="primary"
                  />
                </StyledTableHeaderCell>
                <StyledTableHeaderCell>
                  <Typography variant="h6" color="text.secondary">
                    Investor name
                  </Typography>
                </StyledTableHeaderCell>
                {!isCapitalAndLoan ? (
                  <StyledTableHeaderCell>
                    <Typography variant="h6" color="text.secondary">
                      Total commitment
                    </Typography>
                  </StyledTableHeaderCell>
                ) : (
                  <>
                    <StyledTableHeaderCell>
                      <Typography variant="h6" color="text.secondary">
                        Capital contribution
                      </Typography>
                    </StyledTableHeaderCell>
                    <StyledTableHeaderCell>
                      <Typography variant="h6" color="text.secondary">
                        Loan commitment
                      </Typography>
                    </StyledTableHeaderCell>
                    <StyledTableHeaderCell>
                      <Typography variant="h6" color="text.secondary">
                        Total commitment
                      </Typography>
                    </StyledTableHeaderCell>
                    {showOverrideColumn && (
                      <StyledTableHeaderCell>
                        <Typography variant="h6" color="text.secondary">
                          Manual override
                        </Typography>
                      </StyledTableHeaderCell>
                    )}
                  </>
                )}
              </TableRow>
            </TableHead>
            <StyledTableBody>
              {pendingCloseCommitments?.map(
                ({
                  id,
                  investorName,
                  isCapitalContributionRuleOverride,
                  isCarryPartnerCommitment,
                  totalCommitmentLcy,
                  loanCommitmentLcy,
                  capitalCommitmentLcy,
                }) => {
                  const originalIndex =
                    formCommitments?.findIndex((c) => c.id === id) ?? -1;
                  return (
                    <TableRow key={id}>
                      <StyledTableCell padding="checkbox">
                        <CheckboxCell
                          control={control}
                          disabled={isCarryPartnerCommitment}
                          name={`commitments.${originalIndex}.isSelected`}
                          onChange={(value) => {
                            handleOnChangeIsSelected(originalIndex, value);
                          }}
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        <Row centered spacing="xs">
                          <Avatar
                            text={investorName}
                            size="lg"
                            color="random"
                          />
                          <Typography variant="body1">
                            {investorName}
                          </Typography>
                        </Row>
                      </StyledTableCell>
                      {!isCapitalAndLoan ? (
                        <StyledTableCell>
                          <Controller
                            control={control}
                            name={`commitments.${originalIndex}.totalCommitmentLcy`}
                            render={({
                              field: { value, onChange },
                              fieldState: { error },
                            }) => (
                              <AppNumberInput
                                value={value ?? undefined}
                                onChange={onChange}
                                min={0}
                                decimals={2}
                                helperText={error?.message?.toString()}
                                error={!!error}
                                currencyStartAdornment
                                currency={baseCurrency}
                                fullWidth={false}
                                style={{ width: 150 }}
                              />
                            )}
                          />
                        </StyledTableCell>
                      ) : (
                        <>
                          <StyledTableCell>
                            <Controller
                              control={control}
                              name={`commitments.${originalIndex}.capitalCommitmentLcy`}
                              render={({
                                field: { value },
                                fieldState: { error },
                              }) => (
                                <AppNumberInput
                                  value={value ?? undefined}
                                  onChange={(newValue: number | null) => {
                                    if (
                                      newValue === undefined ||
                                      newValue === null
                                    ) {
                                      return;
                                    }
                                    capitalCommitmentOnChange(
                                      originalIndex,
                                      newValue,
                                      loanCommitmentLcy,
                                    );
                                  }}
                                  min={0}
                                  decimals={2}
                                  helperText={error?.message?.toString()}
                                  error={!!error}
                                  currencyStartAdornment
                                  currency={baseCurrency}
                                  fullWidth={false}
                                  style={{ width: 150 }}
                                  disabled={
                                    !capitalCommitmentIsEditable(
                                      isCapitalContributionRuleOverride ??
                                        false,
                                      isCarryPartnerCommitment,
                                    )
                                  }
                                />
                              )}
                            />
                          </StyledTableCell>
                          <StyledTableCell>
                            <Controller
                              control={control}
                              name={`commitments.${originalIndex}.loanCommitmentLcy`}
                              render={({
                                field: { value },
                                fieldState: { error },
                              }) => (
                                <AppNumberInput
                                  value={value ?? undefined}
                                  onChange={(newValue: number | null) => {
                                    if (
                                      newValue === undefined ||
                                      newValue === null
                                    ) {
                                      return;
                                    }
                                    loanCommitmentOnChange(
                                      originalIndex,
                                      newValue,
                                      capitalCommitmentLcy,
                                    );
                                  }}
                                  min={0}
                                  decimals={2}
                                  helperText={error?.message?.toString()}
                                  error={!!error}
                                  currencyStartAdornment
                                  currency={baseCurrency}
                                  fullWidth={false}
                                  style={{ width: 150 }}
                                  disabled={
                                    !loanCommitmentIsEditable(
                                      isCapitalContributionRuleOverride ??
                                        false,
                                      isCarryPartnerCommitment,
                                    )
                                  }
                                />
                              )}
                            />
                          </StyledTableCell>
                          <StyledTableCell>
                            <Controller
                              control={control}
                              name={`commitments.${originalIndex}.totalCommitmentLcy`}
                              render={({
                                field: { value },
                                fieldState: { error },
                              }) => (
                                <AppNumberInput
                                  value={value ?? undefined}
                                  onChange={(newValue: number | null) => {
                                    if (
                                      newValue === undefined ||
                                      newValue === null
                                    ) {
                                      return;
                                    }
                                    totalCommitmentOnChange(
                                      originalIndex,
                                      newValue,
                                    );
                                  }}
                                  min={0}
                                  decimals={2}
                                  helperText={error?.message?.toString()}
                                  error={!!error}
                                  currencyStartAdornment
                                  currency={baseCurrency}
                                  fullWidth={false}
                                  style={{ width: 150 }}
                                  disabled={
                                    !totalCommitmentIsEditable(
                                      isCapitalContributionRuleOverride ??
                                        false,
                                      isCarryPartnerCommitment,
                                    )
                                  }
                                />
                              )}
                            />
                          </StyledTableCell>
                          {showOverrideColumn && (
                            <StyledTableCell>
                              {showOverrideCheckbox(
                                isCarryPartnerCommitment,
                              ) && (
                                <CheckboxCell
                                  control={control}
                                  name={`commitments.${originalIndex}.isCapitalContributionRuleOverride`}
                                  onChange={(value) => {
                                    capitalContributionRuleOverrideOnChange(
                                      originalIndex,
                                      value,
                                      totalCommitmentLcy,
                                      isCarryPartnerCommitment,
                                    );
                                  }}
                                />
                              )}
                            </StyledTableCell>
                          )}
                        </>
                      )}
                    </TableRow>
                  );
                },
              )}
            </StyledTableBody>
          </StyledTable>
        </TableContainer>
        {(shouldShowFixedCapitalAmountWillIncreaseOnSubmitMessage ||
          shouldShowFixedCapitalAmountWillDecreaseOnSubmitMessage) && (
          <Alert severity="info">
            {shouldShowFixedCapitalAmountWillIncreaseOnSubmitMessage
              ? fixedCapitalAmountWillChangeOnSubmitMessage
              : fixedCapitalAmountWillDecreaseOnSubmitMessage}
          </Alert>
        )}
      </PageSection>
    </>
  );
};

export default CommitmentsTable;
